import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../api/client'
import { lockDeviceScreen, logOffSession, sessionsPageQuery, type DeviceSession } from '../api/devices'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { isRoot } from './respondGuards'

/**
 * Respond > Log off / Lock screen (Securitee > Incidents): cine e logat pe
 * aparatul incidentului, Log off pe fiecare sesiune si Lock screen (LCK, toate
 * sesiunile), fiecare cu confirmare. Aceleasi capete
 * si drepturi ca fila Active Sessions (`DeviceSessions.tsx`, comanda LGF);
 * aici compact, fara istoricul de logari. Fara dreptul de a vedea sesiunile
 * (403) sectiunea nu se deseneaza; fara `can_log_off`, lista fara butoane.
 */
export function RespondSessions({ id, onAction, recommended = null }: { id: number; onAction?: () => void; recommended?: string | null }) {
  const client = useQueryClient()
  const q = useQuery(sessionsPageQuery(id))
  /** Ce asteapta confirmarea: Log off pe o sesiune, sau Lock screen. */
  const [confirm, setConfirm] = useState<DeviceSession | 'lock' | null>(null)
  const [busy, setBusy] = useState(false)

  if (q.error) {
    if (q.error instanceof ApiError && (q.error.status === 403 || q.error.status === 404)) return null
    return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  }
  if (!q.data) return null

  const who = (s: DeviceSession) => (s.domain ? `${s.domain}\\` : '') + s.user_name

  const run = async (what: DeviceSession | 'lock') => {
    if (busy) return
    setBusy(true)
    try {
      if (what === 'lock') {
        await lockDeviceScreen(id)
        toast('Lock screen instruction sent.', 'Success', 'success')
      } else {
        await logOffSession(id, what.user_name, what.id)
        toast('Log off instruction sent.', 'Success', 'success')
      }
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
      setConfirm(null)
      void client.invalidateQueries({ queryKey: sessionsPageQuery(id).queryKey })
      onAction?.()
    }
  }

  return (
    <div className="respond-sessions" style={{ marginTop: 16 }}>
      <h5 style={{ fontWeight: 600, display: 'flex', gap: 12, alignItems: 'center' }}>
        Logged on users
        {q.data.can_log_off ? (
          <button type="button" className={'btn btn-xs btn-default respond-lock' + (recommended === 'lock' ? ' respond-recommended' : '')} disabled={busy} onClick={() => setConfirm('lock')}>
            <i className="fas fa-lock" aria-hidden="true"></i> Lock screen
          </button>
        ) : null}
      </h5>
      {q.data.sessions.length === 0 ? (
        <p className="font-grey-cascade">Nobody is logged on.</p>
      ) : (
        <table className="table table-condensed" style={{ maxWidth: 640 }}>
          <tbody>
            {q.data.sessions.map((s) => (
              <tr key={s.id} className="respond-session">
                <td>{who(s)}</td>
                <td className="font-grey-cascade">{s.login_type}</td>
                <td style={{ textAlign: 'right' }}>
                  {q.data.can_log_off ? (
                    <button
                      type="button"
                      className={'btn btn-xs btn-default respond-logoff' + (recommended === 'logoff' ? ' respond-recommended' : '')}
                      disabled={busy || s.logged_off || isRoot(s.user_name)}
                      title={isRoot(s.user_name) ? 'root cannot be logged off; isolate or shut down the device instead' : undefined}
                      onClick={() => setConfirm(s)}
                    >
                      <i className="fas fa-sign-out-alt" aria-hidden="true"></i> {s.logged_off ? 'Logged off' : 'Log off'}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Modal
        id="respondLogoffConfirm"
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm === 'lock' ? 'Lock screen' : 'Log off user'}
        portal
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setConfirm(null)}>
              Cancel
            </button>
            <button
              type="button"
              className={`btn btn-danger ${confirm === 'lock' ? 'respond-lock-confirm' : 'respond-logoff-confirm'}`}
              disabled={busy}
              onClick={() => confirm && void run(confirm)}
            >
              {confirm === 'lock' ? 'Lock screen' : 'Log off'}
            </button>
          </>
        }
      >
        <p>
          {confirm === 'lock'
            ? 'Lock the screen of every session on this device? Users, at the keyboard or on remote desktop, must sign in again; nothing is closed or lost. It does not stop a remote attacker who does not use the screen; isolate the device for that.'
            : `Log off ${confirm ? who(confirm) : ''} from this device? Unsaved work in the session is lost.`}{' '}
          The instruction applies when the device next contacts the server.
        </p>
      </Modal>
    </div>
  )
}
